import { fork, join, put, retry, takeEvery } from 'redux-saga/effects';
import * as infoWaterProActions from '../actions/infoWaterPro.actions';

import {
	getDSChildren,
	getDSTableMetadata,
	getDSTables,
	getDSResultSets,
	deleteDSChildren,
} from '../services/domainServices.service';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	DSTable,
	DSTableMetadata,
	DSTreeObject,
	PublishedSimulation,
	RequestResultSetsAction,
	ResultSet,
} from '../types';
import {
	deleteInfoWaterProSimulation,
	deleteInfoWaterProSimulationByName,
	deleteInfoWaterProSimulationByNameResolved,
	deleteInfoWaterProSimulationResolved,
	requestInfoWaterProMetadataRejected,
	requestInfoWaterProTables,
	requestInfoWaterProTablesRejected,
	requestInfoWaterProTablesResolved,
} from '../actions/infoWaterPro.actions';
import { Task } from 'redux-saga';
import { generateSimulationTableId } from '../utils';
import { error } from '../actions';
import { unregisterSimulation } from '../services';
const {
	requestInfoWaterProAnalysisGroups,
	requestInfoWaterProAnalysisGroupsResolved,
	requestInfoWaterProAnalysisGroupsRejected,
	requestInfoWaterProSimulations,
	requestInfoWaterProSimulationsResolved,
	requestInfoWaterProSimulationsRejected,
} = infoWaterProActions;

function* requestAnalysisGroupsSaga(action: PayloadAction<{ dbid: string }>) {
	const { dbid } = action.payload;
	try {
		const analysisGroups: DSTreeObject[] | undefined = yield retry(
			5,
			1500,
			getDSChildren,
			dbid,
			'0',
		);
		if (analysisGroups) {
			yield put(
				requestInfoWaterProAnalysisGroupsResolved({
					dbid,
					data: analysisGroups,
				}),
			);
		} else {
			yield put(
				requestInfoWaterProAnalysisGroupsRejected({
					dbid,
					error: 'Failed to retrieve analysis groups',
				}),
			);
		}
	} catch (e) {
		yield put(
			requestInfoWaterProAnalysisGroupsRejected({
				dbid,
				error: 'Failed to retrieve analysis groups',
			}),
		);
	}
}

function* requestSimulations(
	dbid: string,
	analysisGroupId: string,
	filterDuplicates = true,
) {
	try {
		const simulations: DSTreeObject[] | undefined = yield retry(
			5,
			1500,
			getDSChildren,
			dbid,
			analysisGroupId,
		);
		if (simulations) {
			yield put(
				requestInfoWaterProSimulationsResolved({
					dbid,
					analysisGroupId,
					data: simulations,
					filterDuplicates,
				}),
			);
		} else {
			yield put(
				requestInfoWaterProSimulationsRejected({
					dbid,
					analysisGroupId,
					error: 'Failed to retrieve simulations',
				}),
			);
		}
	} catch (e) {
		yield put(
			requestInfoWaterProSimulationsRejected({
				dbid,
				analysisGroupId,
				error: 'Failed to retrieve simulations',
			}),
		);
	}
}

function* requestSimulationsSaga(
	action: PayloadAction<{
		dbid: string;
		analysisGroupId: string;
		filterDuplicates?: boolean;
	}>,
) {
	const { dbid, analysisGroupId, filterDuplicates } = action.payload;
	if (dbid === '' || analysisGroupId === '') {
		return;
	}

	yield requestSimulations(dbid, analysisGroupId, filterDuplicates);
}

function* deleteSimulation(
	dbid: string,
	analysisGroupId: string,
	simulationId: string,
) {
	if (dbid === '' || analysisGroupId === '' || simulationId === '') {
		return;
	}
	try {
		const response = yield retry(
			5,
			1500,
			deleteDSChildren,
			dbid,
			simulationId,
		);
		if (response) {
			yield retry(
				5,
				1500,
				unregisterSimulation,
				dbid,
				analysisGroupId,
				simulationId,
			);
			yield put(
				deleteInfoWaterProSimulationResolved({
					dbid,
					analysisGroupId,
					simulationId,
				}),
			);
		} else {
			yield put(
				error('Failed to delete simulation with oid ' + simulationId),
			);
		}
	} catch (e) {
		yield put(
			error('Failed to delete simulation with oid ' + simulationId),
		);
	}
}

function* deleteSimulationSaga(
	action: PayloadAction<{
		dbid: string;
		analysisGroupId: string;
		simulationId: string;
	}>,
) {
	const { dbid, analysisGroupId, simulationId } = action.payload;
	yield deleteSimulation(dbid, analysisGroupId, simulationId);
}

function* deleteSimulationsByName(
	dbid: string,
	analysisGroupId: string,
	simulationName: string,
) {
	if (dbid === '' || analysisGroupId === '' || simulationName === '') {
		return;
	}
	try {
		const response: DSTreeObject[] | undefined = yield retry(
			5,
			1500,
			getDSChildren,
			dbid,
			analysisGroupId,
		);
		let simulations: DSTreeObject[] = [];
		if (response) {
			simulations = response.filter(
				simulation => simulation.oname === simulationName,
			);
		}
		if (!simulations || simulations.length == 0) {
			return;
		}
		const forkedTasks: Task[] = [];
		for (const sim of simulations) {
			forkedTasks.push(
				yield fork(
					deleteSimulation,
					dbid,
					analysisGroupId,
					sim.oid.toString(),
				),
			);
		}

		// Wait for all forked tasks to complete
		for (const task of forkedTasks) {
			yield join(task);
		}

		yield put(
			deleteInfoWaterProSimulationByNameResolved({
				dbid,
				analysisGroupId,
				simulationName,
			}),
		);
	} catch (e) {
		yield put(
			error('Failed to delete simulation with name ' + simulationName),
		);
	}
}

function* deleteSimulationsByNameSaga(
	action: PayloadAction<{
		dbid: string;
		analysisGroupId: string;
		simulationName: string;
	}>,
) {
	const { dbid, analysisGroupId, simulationName } = action.payload;
	yield deleteSimulationsByName(dbid, analysisGroupId, simulationName);
}

function* requestMultipleSimulationsSaga(
	action: PayloadAction<{
		dbId: string;
		analysisGroups: string[];
	}>,
) {
	const { dbId, analysisGroups } = action.payload;

	const tasks: Task[] = [];
	for (const analysisGroup of analysisGroups) {
		tasks.push(yield fork(requestSimulations, dbId, analysisGroup));
	}

	for (const task of tasks) {
		yield join(task);
	}
}

function* requestTables(dbid: string, simulationId: string) {
	if (dbid === '' || simulationId === '') {
		return;
	}
	try {
		const tables: DSTable[] | undefined = yield retry(
			5,
			1500,
			getDSTables,
			dbid,
			simulationId,
		);
		if (tables) {
			yield put(
				requestInfoWaterProTablesResolved({
					dbid,
					simulationId,
					data: tables,
				}),
			);
		} else {
			yield put(
				requestInfoWaterProTablesRejected({
					dbid,
					simulationId,
					error: 'Failed to retrieve tables',
				}),
			);
		}
	} catch (e) {
		yield put(
			requestInfoWaterProTablesRejected({
				dbid,
				simulationId,
				error: 'Failed to retrieve tables',
			}),
		);
	}
}

function* requestTablesSaga(
	action: PayloadAction<{ dbid: string; simulationId: string }>,
) {
	const { dbid, simulationId } = action.payload;
	yield requestTables(dbid, simulationId);
}

function* requestMetadata(dbid: string, simulationId: string, tableId: string) {
	if (dbid === '' || simulationId === '' || tableId === '') {
		return;
	}
	try {
		const metadata: DSTableMetadata | undefined = yield retry(
			5,
			1500,
			getDSTableMetadata,
			dbid,
			simulationId,
			tableId,
		);
		if (metadata) {
			yield put(
				infoWaterProActions.requestInfoWaterProMetadataResolved({
					dbid,
					simulationId,
					tableId,
					data: metadata,
				}),
			);
		} else {
			yield put(
				requestInfoWaterProMetadataRejected({
					dbid,
					simulationId,
					tableId,
					error: 'Failed to retrieve metadata',
				}),
			);
		}
	} catch (e) {
		yield put(
			requestInfoWaterProMetadataRejected({
				dbid,
				simulationId,
				tableId,
				error: 'Failed to retrieve metadata',
			}),
		);
	}
}

function* requestMetadataSaga(
	action: PayloadAction<{
		dbid: string;
		simulationId: string;
		tableId: string;
	}>,
) {
	const { dbid, simulationId, tableId } = action.payload;
	yield requestMetadata(dbid, simulationId, tableId);
}

function* requestSingleResultSetSaga(
	dbId: string,
	assetType: string,
	objectId: string,
	modellingId: string | undefined,
	publishedSimulations: PublishedSimulation[],
) {
	try {
		const table = generateSimulationTableId(assetType);
		const response: ResultSet[] | undefined = yield retry(
			5,
			1500,
			getDSResultSets,
			dbId,
			table,
			modellingId ?? objectId,
		);

		if (!response) {
			throw new Error();
		}
		const publishedSimulationsIds = publishedSimulations.map(
			({ resultId }) => resultId,
		);
		const resultSetIds = response
			.map(({ resultId }) => resultId)
			.filter(resultId => publishedSimulationsIds.includes(+resultId));

		const forkedTasks: Task[] = [];
		for (const resultSetId of resultSetIds) {
			forkedTasks.push(yield fork(requestTables, dbId, resultSetId));
			forkedTasks.push(
				yield fork(requestMetadata, dbId, resultSetId, table),
			);
		}

		// Wait for all forked tasks to complete
		for (const task of forkedTasks) {
			yield join(task);
		}

		yield put(
			infoWaterProActions.requestResultSetsResolved({
				data: response,
				dbId,
				assetType,
				objectId,
			}),
		);
	} catch (e) {
		yield put(
			infoWaterProActions.requestResultSetsRejected({
				error: 'Failed to retrieve result sets',
				assetType,
				objectId,
				dbId,
			}),
		);
	}
}

function* requestResultSetsSaga(
	action: PayloadAction<RequestResultSetsAction>,
) {
	const { dbId, assets, publishedSimulations } = action.payload;
	const tasks: Task[] = [];
	if (!publishedSimulations || publishedSimulations?.length === 0) {
		return;
	}
	for (const { assetType, assetId, modellingId } of assets) {
		tasks.push(
			yield fork(
				requestSingleResultSetSaga,
				dbId,
				assetType,
				assetId,
				modellingId,
				publishedSimulations,
			),
		);
	}

	for (const task of tasks) {
		yield join(task);
	}
}

function* requestMapSimulationsSaga(
	action: PayloadAction<
		RequestResultSetsAction & { analysisGroups: string[] }
	>,
) {
	yield put(infoWaterProActions.setMapSimulationsLoading(true));
	const { dbId, assets, analysisGroups, publishedSimulations } =
		action.payload;

	const resultSetsTask = yield fork(requestResultSetsSaga, {
		type: infoWaterProActions.requestResultSets.type,
		payload: {
			assets,
			dbId,
			publishedSimulations,
		},
	});

	const simulationsTask = yield fork(requestMultipleSimulationsSaga, {
		type: infoWaterProActions.requestMultipleSimulations.type,
		payload: {
			analysisGroups,
			dbId,
		},
	});

	yield join(resultSetsTask);
	yield join(simulationsTask);
	yield put(infoWaterProActions.setMapSimulationsLoading(false));
}

export function* watchRequestMapSimulations() {
	yield takeEvery(
		infoWaterProActions.requestMapSimulations,
		requestMapSimulationsSaga,
	);
}

export function* watchRequestResultSetsSaga() {
	yield takeEvery(
		infoWaterProActions.requestResultSets,
		requestResultSetsSaga,
	);
}

export function* watchRequestMultipleSimulationsSaga() {
	yield takeEvery(
		infoWaterProActions.requestMultipleSimulations,
		requestMultipleSimulationsSaga,
	);
}

export function* watchRequestAnalysisGroupsSaga() {
	yield takeEvery(
		requestInfoWaterProAnalysisGroups,
		requestAnalysisGroupsSaga,
	);
}

export function* watchRequestSimulationsSaga() {
	yield takeEvery(requestInfoWaterProSimulations, requestSimulationsSaga);
}

export function* watchRequestTablesSaga() {
	yield takeEvery(requestInfoWaterProTables, requestTablesSaga);
}

export function* watchRequestMetadataSaga() {
	yield takeEvery(
		infoWaterProActions.requestInfoWaterProMetadata,
		requestMetadataSaga,
	);
}
export function* watchDeleteSimulationSaga() {
	yield takeEvery(deleteInfoWaterProSimulation, deleteSimulationSaga);
}
export function* watchDeleteSimulationsByNameSaga() {
	yield takeEvery(
		deleteInfoWaterProSimulationByName,
		deleteSimulationsByNameSaga,
	);
}
export default [
	watchRequestAnalysisGroupsSaga(),
	watchRequestSimulationsSaga(),
	watchRequestMapSimulations(),
	watchRequestMultipleSimulationsSaga(),
	watchRequestTablesSaga(),
	watchRequestMetadataSaga(),
	watchRequestResultSetsSaga(),
	watchDeleteSimulationSaga(),
	watchDeleteSimulationsByNameSaga(),
];
